import { createSlice } from '@reduxjs/toolkit';

const emptyThread = {
  id: null,
  conversation: null,
  messages: [],
  // client_msg_id -> 'sending' | 'failed'. Local only; the transcript stays the source of truth.
  pending: {},
  loading: false,
  error: null,
  socket: 'closed',
  acting: false,
  // Transcript paging: the backfill only fetches the newest page.
  page: 1,
  hasMore: false,
  loadingOlder: false,
  customerTyping: false,
};

const initialState = {
  active: false,
  presence: { status: null, canSet: null, pending: false, error: null },
  agentSocket: 'closed',
  reauth: false,
  variant: 'support',
  pool: { items: [], total: 0, loading: false, error: null, claimingId: null },
  inbox: {
    items: [],
    loading: false,
    error: null,
    status: 'all',
    queue: 'general',
    page: 1,
    total: 0,
  },
  agents: [],
  agentsError: null,
  audit: { items: [], loading: false, error: null },
  // Contact-panel reads, each scoped to what it was fetched for so a late response
  // cannot land against a different customer.
  supportHistory: { items: [], loading: false, error: null, conversationId: null },
  deviceCard: { data: null, loading: false, error: null, userId: null },
  thread: { ...emptyThread },
};

const conversationId = (conversation) => conversation?.id || conversation?.conversation_id || null;

// message.new and message.ack arrive in any order; match on client_msg_id, never a map.
const sameMessage = (a, b) =>
  (a.client_msg_id && a.client_msg_id === b.client_msg_id) || (a.id && a.id === b.id);

const byCreatedAt = (a, b) => String(a.created_at || '').localeCompare(String(b.created_at || ''));

const upsertMessage = (thread, message) => {
  const index = thread.messages.findIndex((m) => sameMessage(m, message));
  const merged = index >= 0 ? { ...thread.messages[index], ...message } : message;
  if (index >= 0) thread.messages.splice(index, 1);
  // Almost always the newest: walk back from the end instead of re-sorting the whole array.
  let at = thread.messages.length;
  while (at > 0 && byCreatedAt(thread.messages[at - 1], merged) > 0) at -= 1;
  thread.messages.splice(at, 0, merged);
  if (message.client_msg_id) delete thread.pending[message.client_msg_id];
};

const replaceConversation = (list, conversation) => {
  const id = conversationId(conversation);
  const index = list.findIndex((c) => c.id === id);
  if (index >= 0) list[index] = { ...list[index], ...conversation };
  return index >= 0;
};

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    customerTypingChanged: (state, action) => {
      if (state.thread.id !== action.payload.id) return;
      state.thread.customerTyping = Boolean(action.payload.isTyping);
      for (const item of [...state.inbox.items, ...state.pool.items]) {
        if (item.id === action.payload.id) item.customer_typing = Boolean(action.payload.isTyping);
      }
    },
    consoleOpened: (state, action) => {
      state.active = true;
      state.reauth = false;
      // 'support' reads /conversations/, 'coach' reads /conversations/coach/. The two
      // have different tab vocabularies, so the default tab is reset with the surface.
      state.variant = action.payload === 'coach' ? 'coach' : 'support';
      state.inbox.status = 'all';
      state.pool.error = null;
      state.inbox.error = null;
    },
    consoleClosed: () => initialState,
    reauthRequired: (state) => {
      state.reauth = true;
    },

    getAgentStatusAction: (state) => {
      state.presence.pending = true;
      state.presence.error = null;
    },
    getAgentStatusSuccess: (state, action) => {
      state.presence.status = action.payload.status;
      state.presence.canSet = action.payload.can_set_status;
      state.presence.pending = false;
      state.presence.error = null;
    },
    setAgentStatusAction: (state) => {
      state.presence.pending = true;
      state.presence.error = null;
    },
    setAgentStatusSuccess: (state, action) => {
      state.presence.status = action.payload;
      state.presence.pending = false;
      state.presence.error = null;
    },
    setAgentStatusFailure: (state, action) => {
      state.presence.pending = false;
      state.presence.error = action.payload;
    },
    agentSocketStatusChanged: (state, action) => {
      state.agentSocket = action.payload;
    },
    // Saga-only signal: the debounce watcher turns bursts into one list refresh.
    inboxPushReceived: () => {},

    getPoolAction: (state) => {
      state.pool.loading = true;
      state.pool.error = null;
    },
    getPoolSuccess: (state, action) => {
      // A response landing after consoleClosed must not repopulate reset state.
      if (!state.active) return;
      const { queue, items, total } = action.payload;
      // A response for a queue the agent has since left belongs to that queue.
      if (queue !== state.inbox.queue) return;
      state.pool.items = items;
      state.pool.total = total;
      state.pool.loading = false;
    },
    getPoolFailure: (state, action) => {
      state.pool.loading = false;
      state.pool.error = action.payload;
    },

    setInboxQueue: (state, action) => {
      state.inbox.queue = action.payload;
      state.pool.items = [];
      state.pool.total = 0;
      state.inbox.page = 1;
      state.inbox.items = [];
      state.inbox.loading = true;
      state.inbox.error = null;
    },
    setInboxStatus: (state, action) => {
      state.inbox.status = action.payload;
      state.inbox.page = 1;
      state.inbox.items = [];
      state.inbox.loading = true;
      state.inbox.error = null;
    },
    setInboxPage: (state, action) => {
      state.inbox.page = action.payload;
      state.inbox.items = [];
      state.inbox.loading = true;
      state.inbox.error = null;
    },
    getInboxAction: (state) => {
      state.inbox.loading = true;
      state.inbox.error = null;
    },
    getInboxSuccess: (state, action) => {
      const { status, queue, page, total, items } = action.payload;
      if (!state.active) return;
      // The tab is a server query, so a response that overlapped a tab change belongs
      // to the tab it was fetched for, not the one on screen now.
      if (status !== undefined && state.inbox.status !== status) return;
      if (queue !== undefined && state.inbox.queue !== queue) return;
      if (page !== undefined && state.inbox.page !== page) return;
      state.inbox.total = total ?? items.length;
      state.inbox.items = items;
      state.inbox.loading = false;
    },
    getInboxFailure: (state, action) => {
      state.inbox.loading = false;
      state.inbox.error = action.payload;
    },

    getAgentsAction: () => {},
    getAgentsSuccess: (state, action) => {
      state.agents = action.payload;
      state.agentsError = null;
    },
    getAgentsFailure: (state, action) => {
      state.agents = [];
      state.agentsError = action.payload;
    },

    claimConversationAction: (state, action) => {
      state.pool.claimingId = action.payload;
      state.pool.error = null;
    },
    claimConversationSuccess: (state, action) => {
      if (!state.active) return;
      const id = conversationId(action.payload);
      state.pool.claimingId = null;
      state.pool.items = state.pool.items.filter((c) => c.id !== id);
      state.pool.total = Math.max(0, state.pool.total - 1);
      if (!replaceConversation(state.inbox.items, action.payload)) {
        state.inbox.items.unshift(action.payload);
      }
    },
    // Another agent won the race: drop the row and move on, no error surfaced.
    claimConversationConflict: (state, action) => {
      state.pool.claimingId = null;
      state.pool.items = state.pool.items.filter((c) => c.id !== action.payload);
      state.pool.total = Math.max(0, state.pool.total - 1);
    },
    claimConversationFailure: (state, action) => {
      state.pool.claimingId = null;
      state.pool.error = action.payload;
    },

    openThreadAction: (state, action) => {
      const id = action.payload;
      // Seed from the list so the header has a name before the detail fetch lands.
      const known =
        state.inbox.items.find((c) => c.id === id) || state.pool.items.find((c) => c.id === id);
      const changed = state.thread.id !== id;
      state.thread = {
        ...emptyThread,
        pending: {},
        messages: [],
        id,
        conversation: known || null,
        loading: true,
      };
      // Reopening the open thread fires no rail refetch, so only a real switch clears it.
      if (!changed) return;
      state.supportHistory = { items: [], loading: false, error: null, conversationId: null };
      // The card refetches only when the customer id changes, so keep it for the same customer.
      const customerId = known?.customer?.user_id || known?.customer_id || null;
      if (state.deviceCard.userId !== customerId) {
        state.deviceCard = { data: null, loading: false, error: null, userId: null };
      }
    },
    closeThreadAction: (state, action) => {
      // Close only the named thread (stale completions); no id is the back button.
      const id = action.payload;
      if (id && state.thread.id !== id) return;
      state.thread = { ...emptyThread, pending: {}, messages: [] };
    },
    threadSocketStatusChanged: (state, action) => {
      state.thread.socket = action.payload;
    },
    conversationLoaded: (state, action) => {
      const id = conversationId(action.payload);
      // Merge, never replace: payloads are partial and differ per source, so a
      // later frame that omits the rating must not wipe one already received.
      if (state.thread.id === id) {
        state.thread.conversation = { ...state.thread.conversation, ...action.payload };
      }
      replaceConversation(state.inbox.items, action.payload);
    },
    // The rating lands over the socket when the customer submits it after resolve.
    conversationRated: (state, action) => {
      const { id, rating } = action.payload;
      // Hold the rating even when the detail fetch never landed, rather than dropping it.
      if (state.thread.id === id) {
        state.thread.conversation = { ...state.thread.conversation, rating };
      }
      const row = state.inbox.items.find((c) => c.id === id);
      if (row) row.rating = rating;
    },
    getMessagesSuccess: (state, action) => {
      const { id, messages, page, totalPages } = action.payload;
      if (state.thread.id !== id) return;
      // Newest-first on the wire; the transcript reads oldest-first.
      messages.forEach((message) => upsertMessage(state.thread, message));
      state.thread.loading = false;
      state.thread.loadingOlder = false;
      state.thread.error = null;
      // Keep the deepest page reached: a page-one reconnect backfill must not rewind
      // paging and make "Load older" refetch what is already on screen.
      if (page) state.thread.page = Math.max(state.thread.page || 1, page);
      state.thread.hasMore = Boolean(totalPages) && state.thread.page < totalPages;
    },
    loadOlderAction: (state) => {
      state.thread.loadingOlder = true;
    },
    getMessagesFailure: (state, action) => {
      const { id, message } = action.payload;
      // Guarded like getMessagesSuccess: "Load older" watches its own action, so a
      // late failure must not surface in whichever thread is open now.
      if (id && state.thread.id !== id) return;
      state.thread.loading = false;
      state.thread.loadingOlder = false;
      state.thread.error = message;
    },
    messageReceived: (state, action) => {
      const { id, message } = action.payload;
      if (state.thread.id === id) upsertMessage(state.thread, message);
    },

    sendMessageAction: (state, action) => {
      const { id, message } = action.payload;
      if (state.thread.id !== id) return;
      upsertMessage(state.thread, message);
      state.thread.pending[message.client_msg_id] = 'sending';
      state.thread.error = null;
    },
    retrySendAction: (state, action) => {
      const { id, clientMsgId } = action.payload;
      if (state.thread.id === id) state.thread.pending[clientMsgId] = 'sending';
    },
    sendMessageSuccess: (state, action) => {
      const { id, message } = action.payload;
      if (state.thread.id === id) upsertMessage(state.thread, message);
    },
    sendMessageFailure: (state, action) => {
      const { id, clientMsgId, error, dropMacro } = action.payload;
      if (state.thread.id !== id) return;
      state.thread.pending[clientMsgId] = 'failed';
      state.thread.error = error;
      if (dropMacro) {
        const failed = state.thread.messages.find((m) => m.client_msg_id === clientMsgId);
        if (failed) delete failed.macro_id;
      }
    },

    conversationActionSuccess: (state, action) => {
      const { id, conversation } = action.payload;
      if (!state.active) return;
      // A resolve that lands after the agent moved on must not merge one thread's
      // conversation into another's.
      if (id && state.thread.id !== id) return;
      state.thread.acting = false;
      if (conversation) {
        state.thread.conversation = { ...state.thread.conversation, ...conversation };
      }
    },
    conversationActionFailure: (state, action) => {
      const { id, message } = action.payload;
      if (id && state.thread.id !== id) return;
      state.thread.acting = false;
      state.thread.error = message;
    },
    resolveConversationAction: (state) => {
      state.thread.acting = true;
      state.thread.error = null;
    },
    transferConversationAction: (state) => {
      state.thread.acting = true;
      state.thread.error = null;
    },
    requeueConversationAction: (state) => {
      state.thread.acting = true;
      state.thread.error = null;
    },
    markReadAction: () => {},

    getSupportHistoryAction: (state, action) => {
      state.supportHistory = {
        items: [],
        loading: true,
        error: null,
        conversationId: action.payload?.conversationId || null,
      };
    },
    getSupportHistorySuccess: (state, action) => {
      const { conversationId: forId, items } = action.payload;
      if (state.supportHistory.conversationId !== forId) return;
      state.supportHistory.items = items;
      state.supportHistory.loading = false;
    },
    getSupportHistoryFailure: (state, action) => {
      const { conversationId: forId, message } = action.payload;
      if (state.supportHistory.conversationId !== forId) return;
      state.supportHistory.loading = false;
      state.supportHistory.error = message;
    },
    getDeviceCardAction: (state, action) => {
      state.deviceCard = { data: null, loading: true, error: null, userId: action.payload || null };
    },
    getDeviceCardSuccess: (state, action) => {
      const { userId, data } = action.payload;
      if (state.deviceCard.userId !== userId) return;
      state.deviceCard.data = data;
      state.deviceCard.loading = false;
    },
    getDeviceCardFailure: (state, action) => {
      const { userId, message } = action.payload;
      if (state.deviceCard.userId !== userId) return;
      state.deviceCard.loading = false;
      state.deviceCard.error = message;
    },
    getAuditAction: (state) => {
      // Clear so a new conversation's dialog never shows the previous one's rows.
      state.audit.items = [];
      state.audit.loading = true;
      state.audit.error = null;
    },
    getAuditSuccess: (state, action) => {
      state.audit.items = action.payload;
      state.audit.loading = false;
    },
    getAuditFailure: (state, action) => {
      state.audit.loading = false;
      state.audit.error = action.payload;
    },
  },
});

export const {
  customerTypingChanged,
  setInboxPage,
  consoleOpened,
  consoleClosed,
  reauthRequired,
  getAgentStatusAction,
  getAgentStatusSuccess,
  setAgentStatusAction,
  setAgentStatusSuccess,
  setAgentStatusFailure,
  agentSocketStatusChanged,
  inboxPushReceived,
  getPoolAction,
  getPoolSuccess,
  getPoolFailure,
  setInboxQueue,
  setInboxStatus,
  getInboxAction,
  getInboxSuccess,
  getInboxFailure,
  getAgentsAction,
  getAgentsSuccess,
  getAgentsFailure,
  claimConversationAction,
  claimConversationSuccess,
  claimConversationConflict,
  claimConversationFailure,
  openThreadAction,
  closeThreadAction,
  threadSocketStatusChanged,
  conversationLoaded,
  conversationRated,
  getMessagesSuccess,
  getMessagesFailure,
  loadOlderAction,
  messageReceived,
  sendMessageAction,
  retrySendAction,
  sendMessageSuccess,
  sendMessageFailure,
  conversationActionSuccess,
  conversationActionFailure,
  resolveConversationAction,
  transferConversationAction,
  requeueConversationAction,
  markReadAction,
  getAuditAction,
  getDeviceCardAction,
  getDeviceCardSuccess,
  getDeviceCardFailure,
  getSupportHistoryAction,
  getSupportHistorySuccess,
  getSupportHistoryFailure,
  getAuditSuccess,
  getAuditFailure,
} = chatSlice.actions;

export { chatSlice };
